import { BaseAIClient } from './BaseAIClient';
import type { AIAnalysisRequest, AIAnalysisResult } from '$lib/types/ai';
import { calculateMaxTokens, getDefaultTemperature } from '$lib/utils/tokenUtils';
import { GrammarPromptBuilder } from '../grammar-core/GrammarPromptBuilder';
import { GrammarResponseParser } from '../grammar-core/GrammarResponseParser';
import { ANALYSIS_TEMPERATURE } from '../grammar-core/GrammarConstants';

/**
 * OpenAI API client implementation
 */
export class OpenAIClient extends BaseAIClient {
  private readonly API_URL = 'https://api.openai.com/v1/chat/completions';

  async sendMessage(prompt: string): Promise<string> {
    const body = {
      model: this.model,
      messages: [
        { role: 'user', content: prompt }
      ],
      max_tokens: calculateMaxTokens(prompt, this.model),
      temperature: getDefaultTemperature()
    };

    const data = await this.makeRequest('openai', this.API_URL, body);
    return data.choices[0].message.content;
  }


  async analyzeCorrections(request: AIAnalysisRequest): Promise<AIAnalysisResult[]> {
    const startTime = Date.now();

    try {
      // If no corrections to analyze, return empty array
      if (!request.corrections || request.corrections.length === 0) {
        return [];
      }

      // For large correction sets, process in batches to avoid timeout and response truncation
      const BATCH_SIZE = 5; // Process 5 corrections at a time for fastest first response
      const totalCorrections = request.corrections.length;

      if (totalCorrections > BATCH_SIZE) {
        console.log(`[OpenAI] Processing ${totalCorrections} corrections in batches of ${BATCH_SIZE}`);

        const results: AIAnalysisResult[] = [];
        const batches = Math.ceil(totalCorrections / BATCH_SIZE);

        for (let i = 0; i < batches; i++) {
          const start = i * BATCH_SIZE;
          const end = Math.min(start + BATCH_SIZE, totalCorrections);
          const batchCorrections = request.corrections.slice(start, end);

          console.log(`[OpenAI] Processing batch ${i + 1}/${batches} (corrections ${start + 1}-${end})`);

          const batchRequest = {
            ...request,
            corrections: batchCorrections
          };

          const batchResults = await this.processCorrectionBatch(batchRequest, start);
          results.push(...batchResults);

          // Call progress callback after each batch completes
          if (request.onProgress) {
            request.onProgress(results, {
              current: end,
              total: totalCorrections,
              batchIndex: i + 1,
              totalBatches: batches
            });
          }

          // Add delay between batches to respect server rate limits
          // Server has minute limit set to 9999 (effectively unlimited)
          // Use 100ms delay for fastest response (actual interval = API time ~1-2s + delay 0.1s)
          if (i < batches - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        const responseTime = Date.now() - startTime;
        console.log(`[OpenAI] Completed ${totalCorrections} corrections in ${batches} batches (${responseTime}ms)`);

        return results;
      }

      // For small correction sets, process normally
      return await this.processCorrectionBatch(request, 0);

    } catch (error) {
      console.error('[OpenAI] Analysis failed:', error);
      throw error;
    }
  }

  private async processCorrectionBatch(request: AIAnalysisRequest, indexOffset: number): Promise<AIAnalysisResult[]> {
    const correctionCount = request.corrections.length;

    // Calculate max_tokens based on correction count
    // Each correction needs approximately 120 tokens for response
    const tokensForCorrections = correctionCount * 120;
    const baseTokens = 800; // Base overhead for JSON structure
    let maxTokens = Math.max(calculateMaxTokens(request.contextText, this.model), baseTokens + tokensForCorrections);

    // Cap at model-specific limits
    maxTokens = Math.min(maxTokens, 4000);

    const requestBody = {
      model: this.model,
      messages: GrammarPromptBuilder.buildOpenAIMessages(request, 'en'),
      max_tokens: maxTokens,
      temperature: ANALYSIS_TEMPERATURE
    };

    const response = await fetch(this.API_URL, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[OpenAI] API error - status: ${response.status}`);
      console.error('[OpenAI] Error response:', errorText);

      const errorMessage = GrammarResponseParser.formatErrorMessage('OpenAI', response.status, errorText);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    const aiResponse = data.choices[0].message.content;

    const parsedResult = GrammarResponseParser.parseResponse(aiResponse, 'OpenAI');

    // Adjust correctionIndex to account for batch offset
    if (indexOffset > 0) {
      parsedResult.forEach(result => {
        result.correctionIndex += indexOffset;
      });
    }

    return parsedResult;
  }

  isAvailable(): boolean {
    return !!this.apiKey;
  }

  getModelName(): string {
    return this.model;
  }

}